import { GEO_COLORS } from "../theme";

// The area report's small rules, kept apart from the page so the landing page can link to it
// without loading it, and so they can be tested.

export const AREA_REPORT_PATH = '/area-report';

// The link to a report for a point (EPSG:3857), with the place's name as the user picked it
export const areaReportPath = (x, y, name) => {
    const params = new URLSearchParams({x:String(Math.round(x * 10) / 10), y:String(Math.round(y * 10) / 10)});
    if (name) params.set('n', String(name).slice(0, 120));
    return AREA_REPORT_PATH + '?' + params.toString();
};

// The map's placeDetails (as PointCircleLayer.js sets them) from the overview's details, which
// the price, rent and crime graphs name their lines by
export const placeDetailsOf = (d) => ({
    ...d,
    laname:d.lad25nm, la25code:d.lad25cd, la25name:d.lad25nm,
    regioncode:d.rgn25cd, regionname:d.rgn25nm,
    countrycode:d.ctry25cd, countryname:d.ctry25nm,
});

// The report's heading: the place the user picked, else the neighbourhood's name
export const placeTitle = (name, d) => (name && name.trim()) || d.msoa21hocname || d.wd25nm || d.lad25nm || 'This area';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthName = (ym) => {
    const [y, m] = String(ym || '').split('-');
    return MONTHS[Number(m) - 1] ? MONTHS[Number(m) - 1] + ' ' + y : ym;
};

// The latest month's rent of a kind of home (all homes by default) in the local authority, region and country, from
// ukmap.rentalindex ({areacode: {"YYYY-MM": {"Overall": n, ...}}}): {month, rows}
export const latestRents = (rents, d, propType = 'Overall') => {
    const areas = [[d.lad25cd, d.lad25nm || 'Local authority', GEO_COLORS.lad], [d.rgn25cd, d.rgn25nm || 'Region', GEO_COLORS.rgn],
        [d.ctry25cd, d.ctry25nm || 'Country', GEO_COLORS.ctry]];
    let month = null;
    const rows = [];
    areas.forEach(([code, label, color]) => {
        const s = rents && code && rents[code];
        if (!s) return;
        const last = Object.keys(s).filter((k) => s[k] && s[k][propType] != null).sort().pop();
        if (!last) return;
        if (!month || last > month) month = last;
        rows.push({label, value:Number(s[last][propType]), color, month:last});
    });
    return {month:monthName(month), rows};
};

// The ONS kinds of home the Rents chips offer, in ukmap.rentalindex: by type, then by bedrooms
export const RENT_CHIPS = [['Overall', 'All'], ['Flat/Maisonette', 'Flats'], ['Terraced', 'Terraced'], ['Semi-Detached', 'Semi'],
    ['Detached', 'Detached'], ['1 bed', '1 bed'], ['2 bed', '2 bed'], ['3 bed', '3 bed'], ['4+ bed', '4+ bed']];
// As the Rents sentence names them: "for flats"
export const RENT_CHIP_NAMES = {Overall:'all homes', 'Flat/Maisonette':'flats', Terraced:'terraced houses', 'Semi-Detached':'semi-detached houses',
    Detached:'detached houses', '1 bed':'1 bedroom homes', '2 bed':'2 bedroom homes', '3 bed':'3 bedroom homes', '4+ bed':'homes with 4 or more bedrooms'};

// The chips with a figure in any of the areas' months ({areacode: {"YYYY-MM": {kind: n}}})
export const rentKinds = (rents, codes) => RENT_CHIPS.filter(([k]) => codes.some((c) => {
    const s = rents && c && rents[c];
    return s && Object.values(s).some((m) => m && m[k] != null);
}));

// The change in one area's rent of a kind over 1, 3, 5 and 10 years: its latest month against the
// same month that many years before, [[years, % or null]]
export const RENT_YEARS = [1, 3, 5, 10];
export const rentChanges = (series, kind) => {
    const s = series || {};
    const last = Object.keys(s).filter((k) => s[k] && s[k][kind] != null).sort().pop();
    return RENT_YEARS.map((n) => {
        if (!last) return [n, null];
        const [y, m] = last.split('-');
        const was = s[(Number(y) - n) + '-' + m] && Number(s[(Number(y) - n) + '-' + m][kind]);
        return [n, was > 0 ? (Number(s[last][kind]) - was) / was * 100 : null];
    });
};

// One series as {key: value} by period, indexed to 100 at its first value. Returns the points
// [{i, v (the nominal value), idx}] in the order of periods, skipping periods without a value.
export const indexSeries = (periods, values) => {
    const pts = [];
    let base = null;
    periods.forEach((p, i) => {
        const v = values[p];
        if (v == null || !(Number(v) > 0)) return;
        if (base == null) base = Number(v);
        pts.push({i, v:Number(v), idx:Number(v) / base * 100});
    });
    return pts;
};

// Labels placed beside the ends of lines, moved apart so none overlaps: items [{y}] in pixels,
// gap the least distance between two, within [top, bottom]. Returns the new ys in the same order.
export const spreadLabels = (ys, gap, top, bottom) => {
    const order = ys.map((y, i) => ({y, i})).sort((a, b) => a.y - b.y);
    for (let k = 1; k < order.length; k++) {
        if (order[k].y - order[k - 1].y < gap) order[k].y = order[k - 1].y + gap;
    }
    const over = order.length ? order[order.length - 1].y - bottom : 0;
    if (over > 0) {
        order[order.length - 1].y -= over;
        for (let k = order.length - 2; k >= 0; k--) {
            if (order[k + 1].y - order[k].y < gap) order[k].y = order[k + 1].y - gap;
        }
    }
    if (order.length && order[0].y < top) {
        const shift = top - order[0].y;
        order.forEach((o) => { o.y += shift; });
    }
    const out = new Array(ys.length);
    order.forEach((o) => { out[o.i] = o.y; });
    return out;
};

/* ---------- the PDF's indexed charts ---------- */

// The price graph's line names (getAreaNameFromType in Layers/PropertySalesGraphLayer2.js):
// 'Local' for the area itself, else the area's name cut to 15 characters
const salesAreaName = (areatype, pd) => {
    const name = areatype === 'local' ? 'Local' : areatype === 'lad' ? pd.laname : areatype === 'region' ? pd.regionname : pd.countryname;
    return name && name.length > 15 ? name.substring(0, 15) + '...' : name;
};
const SALES_AREAS = [['local', 'Local area', GEO_COLORS.local], ['lad', null, GEO_COLORS.lad], ['region', null, GEO_COLORS.rgn],
    ['country', null, GEO_COLORS.ctry]];

// From the price graph's traces (buildSalesTraces): one line per area for a metric and property
// type (All, Flats or Houses), over the last years: {periods, lines}
export const salesLines = (traces, pd, metric, propType, years = 10) => {
    const rows = (traces && traces[metric]) || [];
    const recent = rows.slice(-years * 4);
    const periods = recent.map((r) => r.quarter);
    const lines = SALES_AREAS.map(([a, label, color]) => {
        const key = salesAreaName(a, pd) + ' ' + propType;
        const values = {};
        recent.forEach((r) => { if (r[key] != null) values[r.quarter] = r[key]; });
        return {key:a, label:label || (a === 'lad' ? pd.laname : a === 'region' ? pd.regionname : pd.countryname) || a, color, values, strong:a === 'local'};
    });
    return {periods, lines};
};

const CRIME_AREAS = [['oa', 'Local area', GEO_COLORS.local], ['lad', null, GEO_COLORS.lad], ['rgn', null, GEO_COLORS.rgn], ['ctry', null, GEO_COLORS.ctry]];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const crimeMonthKey = (m) => { const [mon, y] = m.split(' '); return Number(y) * 100 + SHORT_MONTHS.indexOf(mon); };

// All crimes per 100 people each month, as a trailing 3 month average, from the crime graph's
// series (ukmap.get_street_crime_timeseries): {periods, lines}
export const crimeLines = (series, pd) => {
    const geos = CRIME_AREAS.filter(([k]) => series && series[k] && series[k].population && series[k].crimes);
    const months = [...new Set(geos.flatMap(([k]) => Object.keys(series[k].crimes)))].sort((a, b) => crimeMonthKey(a) - crimeMonthKey(b));
    const lines = geos.map(([k, label, color]) => {
        const s = series[k];
        const rate = months.map((m) => Object.values(s.crimes[m] || {}).reduce((t, v) => t + (Number(v) || 0), 0) / s.population * 100);
        const values = {};
        months.forEach((m, i) => { if (i >= 2) values[m] = (rate[i - 2] + rate[i - 1] + rate[i]) / 3; });
        return {key:k, label:label || (k === 'lad' ? pd.laname : k === 'rgn' ? pd.regionname : pd.countryname) || k, color, values, strong:k === 'oa'};
    });
    return {periods:months.slice(2), lines};
};

// Private rents for one kind of home in the local authority, region and country, every month: {periods, lines}
export const rentLines = (rents, d, propType = 'Overall') => {
    const areas = [[d.lad25cd, d.lad25nm || 'Local authority', GEO_COLORS.lad], [d.rgn25cd, d.rgn25nm || 'Region', GEO_COLORS.rgn],
        [d.ctry25cd, d.ctry25nm || 'Country', GEO_COLORS.ctry]].filter(([code]) => rents && code && rents[code]);
    const periods = [...new Set(areas.flatMap(([code]) => Object.keys(rents[code])))].sort();
    const lines = areas.map(([code, label, color], i) => {
        const values = {};
        periods.forEach((p) => { const v = rents[code][p] && rents[code][p][propType]; if (v != null) values[p] = Number(v); });
        return {key:code, label, color, values, strong:i === 0};
    });
    return {periods, lines};
};

// Axis labels for about six evenly spaced periods, first and last included
export const sparseLabels = (periods, label, count = 6) => {
    const n = periods.length;
    const every = Math.max(1, Math.round((n - 1) / (count - 1)));
    return (p, i) => (i === 0 || i === n - 1 || (i % every === 0 && n - 1 - i >= every * 0.75)) ? label(p) : null;
};
